import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/dist/query';
import type {
  CreateQuickViewApiArg,
  CreateQuickViewApiResponse,
  Dashboard,
  DeleteQuickViewApiArg,
  DeleteQuickViewApiResponse,
  QuickView,
  ReadComparisonInstrumentsApiArg,
  ReadComparisonInstrumentsApiResponse,
  ReadDashboardsApiResponse,
  ReadFiltersApiArg,
  ReadFiltersApiResponse,
  ReadInstrumentsApiArg,
  ReadInstrumentsApiResponse,
  ReadPeriodPresetsApiArg,
  ReadPeriodPresetsApiResponse,
  ReadQuickViewApiArg,
  ReadQuickViewApiResponse,
  ReadQuickViewsApiArg,
  ReadQuickViewsApiResponse,
  UpdateQuickViewApiArg,
  UpdateQuickViewApiResponse,
  UpdateQuickViewFavoriteStateApiArg,
  UpdateQuickViewFavoriteStateApiResponse,
} from 'lib-analytics-types';
import { paginatedQuery } from 'lib-shared-api-utils';
import unionBy from 'lodash/unionBy';
import moment from 'moment-timezone';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import {
  transformCreateUpdateQuickViewArg,
  transformReadInstrumentsArg,
} from './utils/transformArg';
import {
  transformReadDashboardsResponse,
  transformReadFiltersResponse,
  transformReadInstrumentsResponse,
} from './utils/transformResponse';

export const analyticsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readDashboards: builder.query<ReadDashboardsApiResponse, void>({
      query: () => ({
        version: 1,
        path: 'analytics/dashboards',
      }),
      transformResponse: transformReadDashboardsResponse,
    }),
    readFilters: builder.query<ReadFiltersApiResponse, ReadFiltersApiArg>({
      query: ({ id, ...body }) => ({
        version: 1,
        path: `analytics/dashboards/${id}/filters`,
        method: 'POST',
        body,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const {
            data: {
              data: { filters },
            },
          } = await queryFulfilled;

          // We're adding the filters data to the corresponding dashboard in the `readDashboards`
          // query state, so that we have easy access to them when working with the dashboard.
          dispatch(
            analyticsApi.util.updateQueryData(
              'readDashboards',
              undefined,
              (draft) => {
                const dashboard = draft.data.dashboards.find(
                  (d) => d.id === id,
                ) as DeepWritable<Dashboard>;

                if (dashboard) {
                  dashboard.filters = unionBy(
                    filters as DeepWritable<typeof filters>,
                    dashboard.filters,
                    'field',
                  );
                }
              },
            ),
          );
        } catch {
          /* do nothing */
        }
      },
      transformResponse: transformReadFiltersResponse,
    }),
    readInstruments: builder.query<
      ReadInstrumentsApiResponse,
      ReadInstrumentsApiArg
    >({
      query: (arg) => ({
        version: 1,
        path: `analytics/panels/${arg.id}/instruments`,
        method: 'POST',
        body: transformReadInstrumentsArg(arg),
      }),
      transformResponse: transformReadInstrumentsResponse,
    }),
    readComparisonInstruments: builder.query<
      ReadComparisonInstrumentsApiResponse,
      ReadComparisonInstrumentsApiArg
    >({
      query: ({ payload }) => ({
        version: 1,
        path: `analytics/panels/${payload.id}/instruments`,
        method: 'POST',
        body: transformReadInstrumentsArg(payload),
      }),
      serializeQueryArgs: ({
        queryArgs: { side },
        endpointDefinition,
        endpointName,
      }) => {
        return defaultSerializeQueryArgs({
          endpointName,
          queryArgs: { side },
          endpointDefinition,
        });
      },
      // Removes the cached data immediately after the last component unsubscribes. This is
      // necessary to ensure that no stale data remains when the user compares different panels.
      keepUnusedDataFor: 0,
    }),
    readPeriodPresets: builder.query<
      ReadPeriodPresetsApiResponse,
      ReadPeriodPresetsApiArg
    >({
      query: ({ timezone }) => ({
        version: 1,
        path: `analytics/presets`,
        method: 'POST',
        body: {
          timezone: timezone ?? moment.tz.guess(),
        },
      }),
    }),
    readQuickViews: builder.query<
      ReadQuickViewsApiResponse,
      ReadQuickViewsApiArg
    >({
      query: (arg) =>
        paginatedQuery(arg, () => ({
          version: 1,
          path: `analytics/quick-views`,
          params: {
            type: 'all',
          },
        })),
    }),
    readFavoriteQuickViews: builder.query<ReadQuickViewsApiResponse, void>({
      query: () =>
        paginatedQuery(
          {
            limit: 20,
            offset: 0,
            sort: {
              key: 'name',
              direction: 'asc',
            },
          },
          () => ({
            version: 1,
            path: `analytics/quick-views`,
            params: {
              type: 'favorite',
            },
          }),
        ),
    }),
    readQuickView: builder.query<ReadQuickViewApiResponse, ReadQuickViewApiArg>(
      {
        query: ({ dashboardId, id }) => ({
          version: 1,
          path: `analytics/dashboards/${dashboardId}/quick-views/${id}`,
        }),
      },
    ),
    createQuickView: builder.mutation<
      CreateQuickViewApiResponse,
      CreateQuickViewApiArg
    >({
      query: ({ dashboardId, quickView }) => ({
        version: 1,
        path: `analytics/dashboards/${dashboardId}/quick-views`,
        method: 'POST',
        body: {
          ...transformCreateUpdateQuickViewArg(quickView),
          dashboard_id: dashboardId,
        },
      }),
    }),
    updateQuickView: builder.mutation<
      UpdateQuickViewApiResponse,
      UpdateQuickViewApiArg
    >({
      query: ({ dashboardId, id, quickView }) => ({
        version: 1,
        path: `analytics/dashboards/${dashboardId}/quick-views/${id}`,
        method: 'PATCH',
        body: {
          ...transformCreateUpdateQuickViewArg(quickView),
          dashboard_id: dashboardId,
        },
      }),
      async onQueryStarted(
        { id, dashboardId, quickView },
        { dispatch, queryFulfilled },
      ) {
        const patchReadFavoriteQuickViews = dispatch(
          analyticsApi.util.updateQueryData(
            'readFavoriteQuickViews',
            undefined,
            (draft) => {
              const mutableQuickViews = draft.data.quick_views as DeepWritable<
                typeof draft.data.quick_views
              >;
              const index = draft.data.quick_views.findIndex(
                (_quickView) => _quickView.id === id,
              );

              if (index !== -1) {
                mutableQuickViews[index] = {
                  ...mutableQuickViews[index],
                  ...(quickView as DeepWritable<QuickView>),
                };
              }
            },
          ),
        );

        const patchReadQuickView = dispatch(
          analyticsApi.util.updateQueryData(
            'readQuickView',
            { id, dashboardId },
            (draft) => {
              const mutableQuickView = draft.data.quick_view as DeepWritable<
                typeof draft.data.quick_view
              >;

              for (const [key, value] of Object.entries(quickView)) {
                mutableQuickView[key] = value;
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadFavoriteQuickViews.undo();
          patchReadQuickView.undo();
        }
      },
    }),
    deleteQuickView: builder.mutation<
      DeleteQuickViewApiResponse,
      DeleteQuickViewApiArg
    >({
      query: ({ dashboardId, id }) => ({
        version: 1,
        path: `analytics/dashboards/${dashboardId}/quick-views/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchReadFavoriteQuickViews = dispatch(
          analyticsApi.util.updateQueryData(
            'readFavoriteQuickViews',
            undefined,
            (draft) => {
              const mutableQuickViews = draft.data.quick_views as DeepWritable<
                typeof draft.data.quick_views
              >;
              const index = draft.data.quick_views.findIndex(
                (_quickView) => _quickView.id === id,
              );

              if (index !== -1) {
                mutableQuickViews.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadFavoriteQuickViews.undo();
        }
      },
    }),
    updateQuickViewFavoriteState: builder.mutation<
      UpdateQuickViewFavoriteStateApiResponse,
      UpdateQuickViewFavoriteStateApiArg
    >({
      query: ({ quickView, favorited }) => ({
        version: 1,
        path: `analytics/dashboards/${quickView.dashboard_id}/quick-views/${quickView.id}/favorite`,
        method: favorited ? 'POST' : 'DELETE',
      }),
      async onQueryStarted(
        { quickView, favorited },
        { dispatch, queryFulfilled },
      ) {
        const patchReadFavoriteQuickViews = dispatch(
          analyticsApi.util.updateQueryData(
            'readFavoriteQuickViews',
            undefined,
            (draft) => {
              const mutableQuickViews = draft.data.quick_views as DeepWritable<
                QuickView[]
              >;

              if (favorited) {
                mutableQuickViews.push({
                  ...(quickView as DeepWritable<QuickView>),
                  favorited,
                });
              } else {
                mutableQuickViews.splice(
                  mutableQuickViews.findIndex(({ id }) => id === quickView.id),
                  1,
                );
              }
            },
          ),
        );

        const patchReadQuickView = dispatch(
          analyticsApi.util.updateQueryData(
            'readQuickView',
            { id: quickView.id, dashboardId: quickView.dashboard_id },
            (draft) => {
              const mutableQuickView = draft.data.quick_view as DeepWritable<
                typeof draft.data.quick_view
              >;

              mutableQuickView.favorited = favorited;
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadFavoriteQuickViews.undo();
          patchReadQuickView.undo();
        }
      },
    }),
  }),
});
